import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const SORTS = [
  ["trending", "Trending"], ["newest", "Newest"], ["popular", "Most Popular"],
  ["value", "Highest Value"], ["shipping", "Lowest Shipping"], ["ending", "Ending Soon"],
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const sort = params.get("sort") || "trending";
  const q = params.get("q") || "";
  const maxShip = params.get("max_shipping") || "";

  const setParam = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (brand) qs.set("brand", brand);
    if (sort) qs.set("sort", sort);
    if (q) qs.set("q", q);
    if (maxShip) qs.set("max_shipping", maxShip);
    api.get(`/products?${qs.toString()}`).then((r) => { setProducts(r.data); setLoading(false); });
  }, [category, brand, sort, q, maxShip]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); api.get("/brands").then((r) => setBrands(r.data)); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-stone-900">Shop Friibs</h1>
        <p className="text-stone-500 mt-1">Try products for free. Just cover shipping.</p>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* FILTERS */}
        <aside className="space-y-6" data-testid="shop-filters">
          <div>
            <h3 className="font-display font-bold text-stone-900 mb-3 flex items-center gap-2"><SlidersHorizontal size={16} /> Category</h3>
            <div className="flex flex-wrap lg:flex-col gap-2">
              <button onClick={() => setParam("category", "")} className={`text-left px-3 py-1.5 rounded-full text-sm font-semibold ${!category ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>All</button>
              {cats.map((c) => (
                <button key={c.slug} data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.slug)}
                  className={`text-left px-3 py-1.5 rounded-full text-sm font-semibold ${category === c.slug ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-display font-bold text-stone-900 mb-3">Brand</h3>
            <select value={brand} onChange={(e) => setParam("brand", e.target.value)} data-testid="filter-brand"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 bg-white text-sm">
              <option value="">All brands</option>
              {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <h3 className="font-display font-bold text-stone-900 mb-3">Max shipping</h3>
            <select value={maxShip} onChange={(e) => setParam("max_shipping", e.target.value)} data-testid="filter-shipping"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 bg-white text-sm">
              <option value="">Any</option><option value="5">Under $5</option><option value="7">Under $7</option><option value="10">Under $10</option>
            </select>
          </div>
        </aside>

        {/* RESULTS */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <span className="text-sm text-stone-500">{products.length} Friibs</span>
            <select value={sort} onChange={(e) => setParam("sort", e.target.value)} data-testid="sort-select"
              className="px-4 py-2 rounded-full border border-stone-200 bg-white text-sm font-semibold">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {loading ? <div className="py-20 text-center text-stone-400">Loading Friibs…</div> :
            products.length === 0 ? <div className="py-20 text-center text-stone-400">No Friibs found. Try another filter.</div> :
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {products.map((p, i) => <ProductCard key={p.slug} p={p} index={i} />)}
            </div>}
        </div>
      </div>
    </div>
  );
}
